import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SvgIcon } from '@components-library/Icons/SvgIcon';
import { KeyMapKeys } from '@locales/lokalise/keyMap';
import SuccessIcon from 'assets/icons/success-icon.svg';
import ErrorIcon from 'assets/icons/warning-message-icon.svg';
import cn from 'classnames';
import i18next from 'i18next';
import styles from './notification.style.module.scss';

type NotificationState = {
  content: string | null;
  type: '' | 'success' | 'error';
};

interface StaticNotificationProps {
  show: (
    content: string | null,
    type?: NotificationState['type'],
    options?: { onCloseCallback?: () => void; notificationShowTime?: number },
    showTimeNumber?: number,
  ) => void;
}

const Notification: React.FC & StaticNotificationProps = () => {
  const timeoutIdRef = useRef<number | null | NodeJS.Timeout>(null);
  const [content, setContent] = useState<string | null>();
  const [type, setType] = useState<'' | 'success' | 'error'>('');

  const show = useCallback<StaticNotificationProps['show']>(
    (content, type = 'success', options = {}, showTimeNumber): void => {
      const { onCloseCallback, notificationShowTime } = options;
      const DEFAULT_NOTIFICATION_SHOW_TIME = showTimeNumber || 5000;

      timeoutIdRef.current = null;
      setContent(content);
      setType(type);

      timeoutIdRef.current = setTimeout(() => {
        close();
        onCloseCallback?.();
      }, notificationShowTime ?? DEFAULT_NOTIFICATION_SHOW_TIME);
    },
    [],
  );

  useEffect(() => {
    Notification.show = show;
  }, [show]);

  const close = (): void => {
    setContent(null);
    setType('');

    timeoutIdRef.current = null;
  };

  const renderIcon = (type: NotificationState['type']): JSX.Element => {
    switch (type) {
      case 'success':
        return <SvgIcon src={SuccessIcon} />;
      case 'error':
        return <SvgIcon src={ErrorIcon} />;
      default:
        return <SvgIcon src={SuccessIcon} />;
    }
  };

  const notificationText = content && i18next.exists(content) ? i18next.t(content as KeyMapKeys) : content;

  return !content ? null : (
    <div className={cn(styles.notification, styles[type])} role="presentation" onClick={close}>
      <div className={styles.icon}>{renderIcon(type)}</div>
      <div className={styles.text}>{notificationText}</div>
    </div>
  );
};

Notification.show = () => console.error('method show is not defined');

export { Notification };
